// Bootstrap do i18n da SPA: idioma inicial (preferência local → navegador → pt-BR), <html lang> e persistência.
import {
  LOCALE_STORAGE_KEY,
  type Locale,
  createI18n,
  isLocale,
  resolveInitialLocale,
} from '@giyus/i18n';
import type { i18n as I18nInstance } from 'i18next';

export interface I18nHost {
  readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  readonly languages: readonly string[];
  readonly setDocumentLang: (locale: Locale) => void;
}

function readStoredLocale(storage: I18nHost['storage']): string | null {
  try {
    return storage?.getItem(LOCALE_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeStoredLocale(storage: I18nHost['storage'], locale: Locale): void {
  try {
    storage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // armazenamento indisponível (modo privado): conveniência local, nunca requisito
  }
}

export function detectInitialLocale(host: Pick<I18nHost, 'storage' | 'languages'>): Locale {
  return resolveInitialLocale([readStoredLocale(host.storage), ...host.languages]);
}

/** Inicializa o i18next para a aplicação e sincroniza `<html lang>` + preferência local. */
export async function initAppI18n(host: I18nHost): Promise<I18nInstance> {
  const initial = detectInitialLocale(host);
  const i18n = await createI18n({ lng: initial });
  host.setDocumentLang(initial);
  i18n.on('languageChanged', (lng) => {
    if (isLocale(lng)) {
      host.setDocumentLang(lng);
      writeStoredLocale(host.storage, lng);
    }
  });
  return i18n;
}

export function browserI18nHost(): I18nHost {
  return {
    storage: typeof localStorage === 'undefined' ? null : localStorage,
    languages: typeof navigator === 'undefined' ? [] : navigator.languages,
    setDocumentLang: (locale) => {
      document.documentElement.lang = locale;
    },
  };
}
